import { FunctionComponent, useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
} from '@material-ui/core'
import { MapPin, Plus, Trash2 } from 'react-feather'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'

interface Location {
  id: number | null
  name: string
  latitude: number | null
  longitude: number | null
  address: string | null
  relevantText: string | null
}

const Locations: FunctionComponent = () => {
  const styles = useStyles()
  const [loading, setLoading] = useState(true)
  const [locations, setLocations] = useState<Location[]>([])
  const [savingLocationId, setSavingLocationId] = useState<number | null>(null)
  const [newLocation, setNewLocation] = useState<Location | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get('/locations')
        setLocations(resp.data || [])
      } catch (error) {
        toast.error('Failed to load locations')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const handleLocationChange = (locationId: number, field: keyof Location, value: string | number | null) => {
    setLocations(locations.map(loc =>
      loc.id === locationId ? { ...loc, [field]: value } : loc
    ))
  }

  const handleSaveLocation = async (location: Location) => {
    if (!location.id) return
    setSavingLocationId(location.id)
    try {
      const payload = {
        location: {
          name: location.name,
          address: location.address,
          relevant_text: location.relevantText,
        }
      }
      const resp = await axios.put(`/locations/${location.id}`, payload)
      // Update location with coordinates from backend (geocoded from address)
      setLocations(locations.map(loc =>
        loc.id === location.id ? {
          ...loc,
          latitude: resp.data.latitude,
          longitude: resp.data.longitude,
        } : loc
      ))
      toast.success('Location updated - wallet passes will be refreshed')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to save location'
      toast.error(message)
    } finally {
      setSavingLocationId(null)
    }
  }

  const handleAddLocation = async () => {
    if (!newLocation?.name || !newLocation?.address) {
      toast.error('Please enter a location name and address')
      return
    }
    try {
      const payload = {
        location: {
          name: newLocation.name,
          address: newLocation.address,
          relevant_text: newLocation.relevantText,
        }
      }
      const resp = await axios.post('/locations', payload)
      setLocations([...locations, resp.data])
      setNewLocation(null)
      if (resp.data.latitude && resp.data.longitude) {
        toast.success('Location added with coordinates')
      } else {
        toast.warning('Location added but address could not be geocoded - please verify the address')
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to add location'
      toast.error(message)
    }
  }

  const handleDeleteLocation = async (locationId: number) => {
    if (!window.confirm('Delete this location? This will remove it from all wallet passes.')) return
    try {
      await axios.delete(`/locations/${locationId}`)
      setLocations(locations.filter(loc => loc.id !== locationId))
      toast.success('Location deleted')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete location'
      toast.error(message)
    }
  }

  return (
    <Box p={2}>
      <Box mb={2} display="flex" alignItems="center" style={{ gap: 8 }}>
        <MapPin size={24} />
        <Box>
          <Typography variant={'h3'}>Locations</Typography>
          <Typography variant={'caption'}>
            Your business locations. When members are near one, their wallet pass appears on their lock screen.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Box className={styles.list}>
          {locations.length === 0 && !newLocation && (
            <Typography variant="body2" color="textSecondary">
              No locations configured. Add a location to enable lock screen notifications.
            </Typography>
          )}

          {locations.map((location) => (
            <Box key={location.id} className={styles.locationCard}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="subtitle2">{location.name}</Typography>
                <IconButton
                  size="small"
                  onClick={() => location.id && handleDeleteLocation(location.id)}
                  title="Delete location"
                >
                  <Trash2 size={16} />
                </IconButton>
              </Box>
              <TextField
                size="small"
                fullWidth
                label="Address"
                placeholder="123 Main St, Seattle, WA 98101"
                value={location.address ?? ''}
                onChange={(e) => location.id && handleLocationChange(location.id, 'address', e.target.value || null)}
                style={{ marginTop: 8 }}
                helperText="Enter address - coordinates will be calculated automatically"
              />
              {location.latitude !== null && location.longitude !== null && (
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                  Coordinates: {location.latitude?.toFixed(5)}, {location.longitude?.toFixed(5)}
                </Typography>
              )}
              <TextField
                size="small"
                fullWidth
                label="Lock Screen Message"
                placeholder="Welcome! Show this pass for your benefits"
                value={location.relevantText ?? ''}
                onChange={(e) => location.id && handleLocationChange(location.id, 'relevantText', e.target.value || null)}
                style={{ marginTop: 8 }}
              />
              <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleSaveLocation(location)}
                  disabled={savingLocationId === location.id || !location.address}
                >
                  {savingLocationId === location.id ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          ))}

          {newLocation ? (
            <Box className={styles.locationCard} style={{ borderStyle: 'dashed' }}>
              <TextField
                size="small"
                fullWidth
                label="Location Name"
                placeholder="e.g., Downtown Store"
                value={newLocation.name}
                onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                autoFocus
              />
              <TextField
                size="small"
                fullWidth
                label="Address"
                placeholder="123 Main St, Seattle, WA 98101"
                value={newLocation.address ?? ''}
                onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value || null })}
                style={{ marginTop: 8 }}
                helperText="Enter address - coordinates will be calculated automatically"
              />
              <TextField
                size="small"
                fullWidth
                label="Lock Screen Message"
                placeholder="Welcome! Show this pass for your benefits"
                value={newLocation.relevantText ?? ''}
                onChange={(e) => setNewLocation({ ...newLocation, relevantText: e.target.value || null })}
                style={{ marginTop: 8 }}
              />
              <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                <Button size="small" onClick={() => setNewLocation(null)}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleAddLocation}
                  disabled={!newLocation.name || !newLocation.address}
                >
                  Add Location
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => setNewLocation({
                id: null,
                name: '',
                latitude: null,
                longitude: null,
                address: null,
                relevantText: null
              })}
              style={{ marginTop: 12 }}
            >
              Add Location
            </Button>
          )}

          {locations.length > 0 && (
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 12 }}>
              Apple Wallet supports up to 10 locations per pass. Pass appears on lock screen within ~100m of each location.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  list: {
    maxWidth: 640,
  },
  locationCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    backgroundColor: '#fff',
  },
})

export default Locations
